import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Фильтры, поиск и страница списка — в адресной строке: ссылку на
 * отфильтрованный список можно переслать, «назад» возвращает к нему.
 * Любая правка фильтра, кроме самой страницы, сбрасывает страницу на первую.
 */
export function useListParams<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const values = Object.fromEntries(keys.map((k) => [k, params.get(k)])) as Record<K, string | null>;
  const page = Number(params.get('page') ?? '1') || 1;

  const set = useCallback(
    (patch: Partial<Record<K | 'page', string | null>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch) as [string, string | null][]) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          if (!('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { values, page, set };
}

/**
 * Мультивыбор фильтра в адресной строке — через запятую: `?cityId=a,b`.
 * Сервер принимает и так, и повтором параметра (доработка M2-1 п. 6).
 */
export const listOf = (value: string | null): string[] => (value ? value.split(',').filter(Boolean) : []);
export const joinList = (values: string[]): string | null => (values.length ? values.join(',') : null);
